// src/utils/renderFrontCoverThumbnail.js
// Render the FRONT FACE of a wraparound cover design to a PNG data URL.
//
// The wraparound canvas is laid out as: [back | spine | front], where front
// is the rightmost faceW pixels. We load the saved Fabric JSON onto an
// offscreen canvas at the original dimensions, then export only the front
// region using fabric's toDataURL crop options.

import * as fabric from 'fabric';

// Mirrors hooks/useCanvasSetup.js — keep in sync if those tables change.
// All sizes at exactly 66 px/inch.
const BOOK_SIZE_DIMS = {
  half_letter: { w: 363, h: 561, realW: 5.5,   realH: 8.5   },
  trade_6x9:  { w: 396, h: 594, realW: 6,     realH: 9     },
  a5:         { w: 385, h: 546, realW: 5.83,  realH: 8.27  },
  pocket_4x7: { w: 281, h: 454, realW: 4.25,  realH: 6.875 },
  us_letter:  { w: 561, h: 726, realW: 8.5,   realH: 11    },
};
const BINDING_SPINE_W = {
  perfect:       100,
  case_bound:    110,
  coil:          60,
  wire:          55,
  saddle_stitch: 12,
};

function resolveFaceDims(design, version) {
  // 1. Design itself stores faceDims (set by useCanvasAutosave on save)
  if (design?.faceDims?.faceW && design?.faceDims?.faceH) {
    return {
      faceW: design.faceDims.faceW,
      faceH: design.faceDims.faceH,
      spineW: design.faceDims.spineW || 0,
    };
  }
  // 2. Compute from version's bookSize + bindingType
  const dims = BOOK_SIZE_DIMS[version?.bookSize] || BOOK_SIZE_DIMS.half_letter;
  const spW = BINDING_SPINE_W[version?.bindingType] || 100;
  return { faceW: dims.w, faceH: dims.h, spineW: spW };
}

/**
 * Render the front face of a wraparound design to a data URL.
 *
 * @param {object|string} design - the wraparoundDesign JSON (or stringified JSON)
 * @param {object} version - the version metadata (used as fallback for face dims)
 * @param {object} [opts]
 * @param {number} [opts.targetWidth=120] - desired output width in pixels
 * @returns {Promise<string|null>} PNG data URL of just the front face, or null on failure
 */
export async function renderFrontCoverThumbnail(design, version, opts = {}) {
  if (!design) return null;
  const targetWidth = opts.targetWidth || 120;

  let parsed = design;
  if (typeof parsed === 'string') {
    try { parsed = JSON.parse(parsed); } catch { return null; }
  }
  if (!parsed || !parsed.objects) return null;

  const { faceW, faceH, spineW } = resolveFaceDims(parsed, version);
  const totalW = faceW * 2 + spineW;

  let off = null;
  try {
    const el = document.createElement('canvas');
    el.width = totalW;
    el.height = faceH;
    off = new fabric.Canvas(el, {
      width: totalW,
      height: faceH,
      backgroundColor: parsed.backgroundColor || parsed.background || '#ffffff',
      renderOnAddRemove: false,
    });
    await off.loadFromJSON(parsed);
    off.backgroundColor = parsed.backgroundColor || parsed.background || '#ffffff';
    off.renderAll();

    // Crop to just the front face (rightmost region) and downscale to thumbnail width.
    const multiplier = targetWidth / faceW;
    return off.toDataURL({
      format: 'png',
      left: faceW + spineW,
      top: 0,
      width: faceW,
      height: faceH,
      multiplier,
    });
  } catch {
    return null;
  } finally {
    if (off) {
      try { off.dispose(); } catch { /* ignore */ }
    }
  }
}
